<template>
  <div class="change-password">
    <h2 class="change-password__title">Смена пароля</h2>
    <p class="change-password__subtitle">
      Придумайте новый пароль длиной от 8 до 32 символов.
    </p>

    <form class="change-password__form" @submit.prevent="handleSubmit">
      <label class="change-password__label" for="old-password">Текущий пароль</label>
      <input
        id="old-password"
        v-model="form.oldPassword"
        class="change-password__input"
        type="password"
        autocomplete="current-password"
        placeholder="Введите текущий пароль"
        :disabled="isSubmitting"
      />

      <label class="change-password__label" for="new-password">Новый пароль</label>
      <input
        id="new-password"
        v-model="form.newPassword"
        class="change-password__input"
        type="password"
        autocomplete="new-password"
        placeholder="Не менее 8 символов"
        :disabled="isSubmitting"
      />

      <label class="change-password__label" for="confirm-password">Повторите новый пароль</label>
      <input
        id="confirm-password"
        v-model="form.confirmPassword"
        class="change-password__input"
        type="password"
        autocomplete="new-password"
        placeholder="Повторите новый пароль"
        :disabled="isSubmitting"
      />

      <p v-if="errorMessage" class="change-password__error">
        {{ errorMessage }}
      </p>
      <BaseButton
        text="Сохранить"
        styles="primary-gradient"
        :is-disabled="isSubmitDisabled"
        vibration="success"
        @on-submit="handleSubmit"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed } from 'vue'
import BaseButton from '@/ui/components/Base/BaseButton.vue'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { useModal } from '@/shared/lib/modal/useModal'
import { BROWSER_LOGIN_URL, BROWSER_LOGIN_URL_DEV, BROWSER_LOGIN_URL_PROD } from '@/shared/config/browserLinks'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { changePassword } = useAuth()
const { openAuthSuccessModal } = useModal()

const form = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const isSubmitting = ref(false)
const errorMessage = ref('')

const isNewPasswordValid = computed(() => {
  return form.newPassword.length >= 8 && form.newPassword.length <= 32
})

const isSubmitDisabled = computed(() => {
  return (
    !form.oldPassword ||
    !form.newPassword ||
    !form.confirmPassword ||
    form.newPassword !== form.confirmPassword ||
    !isNewPasswordValid.value ||
    isSubmitting.value
  )
})

const showSuccessModal = () => {
  openAuthSuccessModal({
    title: 'Пароль обновлён',
    description: 'Используйте новый пароль при следующем входе через браузер.',
    buttonLink: BROWSER_LOGIN_URL,
    secondaryLinks: [
      { label: 'Тест: dev.testbot.podpiska.vip', href: BROWSER_LOGIN_URL_DEV },
      { label: 'Прод: bot.podpiska.vip', href: BROWSER_LOGIN_URL_PROD }
    ]
  })
}

const handleSubmit = async () => {
  if (isSubmitDisabled.value) {
    return
  }

  isSubmitting.value = true
  errorMessage.value = ''
  try {
    const response = await changePassword({
      old_password: form.oldPassword,
      new_password: form.newPassword,
    })

    if (response.success) {
      showSuccessModal()
      emit('close')
    } else {
      errorMessage.value = response.message || 'Не удалось изменить пароль.'
    }
  } catch (error) {
    if (error instanceof Error) {
      errorMessage.value = error.message
    } else {
      errorMessage.value = 'Не удалось изменить пароль.'
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.change-password {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: $neutral-100;

  &__title {
    @include h1;
    margin: 0;
    text-align: center;
  }

  &__subtitle {
    @include p2-regular;
    margin: 0;
    text-align: center;
    color: $neutral-60;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__label {
    @include p2-medium;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    border-radius: 12px;
    border: 2px solid $secondary-200;
    background: $secondary-200;
    color: $neutral-100;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $primary-100;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &::placeholder {
      color: $neutral-60;
    }
  }

  &__error {
    @include p3-regular;
    color: #ff4d4f;
    margin: 4px 0 0;
  }

}
</style>

